import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { VisualizationEvent } from '../components/pitch/types';
import { extractTeamShapePointsFromEvents } from '../utils/teamShapePositionExtraction';
import { 
  kMeansClustering, 
  Cluster, 
  ClusterPoint,
  ZoneConstraint,
  isInGoalkeeperArea,
  calculateGoalkeeperPosition
} from '../utils/clustering';
import { PITCH_WIDTH, PITCH_HEIGHT } from '../components/pitch/utils';
import { shouldIncludeStatType, ShapeType } from '../utils/teamShapeStatFiltering';

// Re-export ShapeType for convenience
export type { ShapeType };

// Simplified stat type interface for team shape (only need id and stat_key)
interface StatTypeMap {
  id: string;
  stat_key: string;
}

export interface TeamShapeConfig {
  mode: 'simple' | 'formation';
  playerCount?: number; // For simple mode (total including goalkeeper)
  formation?: {
    defense: number;
    midfield: number;
    attack: number;
  }; // For formation mode (outfield players only)
  // Separate formations for compare mode
  homeFormation?: {
    defense: number;
    midfield: number;
    attack: number;
  };
  awayFormation?: {
    defense: number;
    midfield: number;
    attack: number;
  };
  // Shape type selection (attacking/defensive/both)
  shapeType?: ShapeType[]; // For single team view
  homeShapeType?: ShapeType[]; // For compare mode - home team
  awayShapeType?: ShapeType[]; // For compare mode - away team
  attackingDirection?: 'left' | 'right'; // Team's attacking direction
  normalizeToFirstHalf?: boolean; // Whether coordinates are normalized to first half
  showNetworks?: boolean; // Show network lines connecting players
}

export interface TeamShapeResult {
  clusters: Cluster[];
  loading: boolean;
  error: string | null;
  pointCount: number;
}

/**
 * Hook for calculating team shape from match visualization events
 */
export const useTeamShape = (
  events: VisualizationEvent[],
  config: TeamShapeConfig,
  teamId?: string,
  shapeTypes?: ShapeType[] // Optional shape types for this specific team (used in compare mode)
): TeamShapeResult => {
  const [statTypes, setStatTypes] = useState<StatTypeMap[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [clusters, setClusters] = useState<Cluster[]>([]);
  const [pointCount, setPointCount] = useState(0);

  // Fetch stat types to build stat_key map
  useEffect(() => {
    const fetchStatTypes = async () => {
      try {
        const { data, error: fetchError } = await supabase
          .from('stat_types')
          .select('id, stat_key')
          .eq('is_active', true);

        if (fetchError) {
          throw fetchError;
        }

        setStatTypes((data || []) as StatTypeMap[]);
      } catch (err) {
        console.error('Error fetching stat types:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch stat types');
      }
    };

    fetchStatTypes();
  }, []);

  // Build stat_key map (stat_type_id -> stat_key)
  const statKeyMap = useMemo(() => {
    const map = new Map<string, string>();
    statTypes.forEach(statType => {
      map.set(statType.id, statType.stat_key);
    });
    return map;
  }, [statTypes]);

  // Calculate team shape
  const calculateTeamShape = useCallback(() => {
    if (statTypes.length === 0 || events.length === 0) {
      setClusters([]);
      setPointCount(0);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Filter events by team if specified
      let filteredEvents = teamId
        ? events.filter(e => e.team_id === teamId)
        : events;

      // Filter events by shape type (attacking/defensive)
      // Use shapeTypes parameter if provided (for compare mode), otherwise use config
      const activeShapeTypes = shapeTypes || config.shapeType || [];
      // Only filter if at least one shape type is selected
      if (activeShapeTypes.length > 0) {
        filteredEvents = filteredEvents.filter(event => {
          const statKey = statKeyMap.get(event.stat_type_id);
          if (!statKey) {
            // If stat key not found, include by default
            return true;
          }
          return shouldIncludeStatType(statKey, activeShapeTypes);
        });
      }

      // Extract position points from events
      const allPoints = extractTeamShapePointsFromEvents(filteredEvents, statKeyMap);
      setPointCount(allPoints.length);

      if (allPoints.length === 0) {
        setClusters([]);
        setLoading(false);
        return;
      }

      // Get attacking direction (default to 'right' if not specified)
      const attackingDirection = config.attackingDirection || 'right';
      const normalizeToFirstHalf = config.normalizeToFirstHalf ?? false;

      // Separate goalkeeper points from outfield player points
      const goalkeeperPoints: ClusterPoint[] = [];
      const outfieldPoints: ClusterPoint[] = [];

      for (const point of allPoints) {
        if (isInGoalkeeperArea(point, attackingDirection, PITCH_WIDTH, PITCH_HEIGHT, normalizeToFirstHalf)) {
          goalkeeperPoints.push(point);
        } else {
          outfieldPoints.push(point);
        }
      }

      // Calculate goalkeeper position (if we have goalkeeper points)
      const goalkeeperCluster = goalkeeperPoints.length > 0
        ? calculateGoalkeeperPosition(goalkeeperPoints, attackingDirection, PITCH_WIDTH, PITCH_HEIGHT, normalizeToFirstHalf)
        : null;

      let resultClusters: Cluster[] = [];

      // Calculate outfield player positions
      if (config.mode === 'simple') {
        // Simple mode: K-Means with specified number of players
        // Always subtract 1 for goalkeeper: if user selects N, we need N-1 outfield players
        // Example: user selects 11 -> 10 outfield + 1 goalkeeper = 11 total
        // Example: user selects 10 -> 9 outfield + 1 goalkeeper = 10 total
        let k = (config.playerCount || 11) - 1; // Subtract 1 for goalkeeper
        
        if (outfieldPoints.length > 0 && k > 0) {
          resultClusters = kMeansClustering(outfieldPoints, k);
        }
      } else if (config.mode === 'formation') {
        // Formation mode: Zone-constrained K-Means
        // Use attacking direction to determine zones based on X-coordinates
        const formation = config.formation || { defense: 4, midfield: 3, attack: 3 };
        
        // Determine zones based on attacking direction using X-coordinates
        // If attacking right: defense is left (low X), attack is right (high X)
        // If attacking left: defense is right (high X), attack is left (low X)
        // Use actual pitch width for zone calculation
        const pitchWidthNormalized = 100; // Normalized for zone calculation
        
        let zones: ZoneConstraint[];
        
        if (attackingDirection === 'right') {
          // Attacking right: defense on left (low X), attack on right (high X)
          zones = [
            {
              minY: 0, // Use Y for filtering, but zones are actually X-based
              maxY: pitchWidthNormalized * 0.33,
              playerCount: formation.defense
            },
            {
              minY: pitchWidthNormalized * 0.33,
              maxY: pitchWidthNormalized * 0.66,
              playerCount: formation.midfield
            },
            {
              minY: pitchWidthNormalized * 0.66,
              maxY: pitchWidthNormalized,
              playerCount: formation.attack
            }
          ];
        } else {
          // Attacking left: defense on right (high X), attack on left (low X)
          zones = [
            {
              minY: pitchWidthNormalized * 0.66,
              maxY: pitchWidthNormalized,
              playerCount: formation.defense
            },
            {
              minY: pitchWidthNormalized * 0.33,
              maxY: pitchWidthNormalized * 0.66,
              playerCount: formation.midfield
            },
            {
              minY: 0,
              maxY: pitchWidthNormalized * 0.33,
              playerCount: formation.attack
            }
          ];
        }

        // Note: constrainedKMeans uses Y-coordinate filtering, but we need X-coordinate
        // For now, we'll filter by X-coordinate manually and then cluster
        // TODO: Update constrainedKMeans to support X-coordinate zones
        const filteredByXZones: ClusterPoint[][] = [];
        for (const zone of zones) {
          const zonePoints = outfieldPoints.filter(p => {
            // Normalize X coordinate to 0-100 range
            const normalizedX = (p.x / PITCH_WIDTH) * 100;
            return normalizedX >= zone.minY && normalizedX <= zone.maxY;
          });
          filteredByXZones.push(zonePoints);
        }

        // Cluster each zone separately
        for (let i = 0; i < zones.length; i++) {
          const zonePoints = filteredByXZones[i];
          if (zonePoints.length > 0 && zones[i].playerCount > 0) {
            const zoneClusters = kMeansClustering(zonePoints, zones[i].playerCount);
            resultClusters.push(...zoneClusters);
          }
        }
      }

      // Add goalkeeper cluster if we have one
      if (goalkeeperCluster) {
        resultClusters.push(goalkeeperCluster);
      }

      setClusters(resultClusters);
      setLoading(false);
    } catch (err) {
      console.error('Error calculating team shape:', err);
      setError(err instanceof Error ? err.message : 'Failed to calculate team shape');
      setLoading(false);
    }
  }, [events, config, teamId, statKeyMap, statTypes.length, shapeTypes]);

  // Recalculate when inputs change
  useEffect(() => {
    calculateTeamShape();
  }, [calculateTeamShape]);

  return {
    clusters,
    loading,
    error,
    pointCount
  };
};

